import React from "react";
import { Link } from "react-router-dom";
import { Search, Database, ShieldCheck, RefreshCw, BookOpen } from "lucide-react";

const PRINCIPLES = [
  {
    icon: Search,
    title: "Independent Research",
    desc: "Articles are researched using primary regulatory documents, blockchain data, market data providers, and company disclosures, not simply summarized from other outlets.",
  },
  {
    icon: Database,
    title: "Verified Market Data",
    desc: "Price, volume, and market data referenced in our coverage is sourced from live exchange feeds and established data providers, cross-checked before publication.",
  },
  {
    icon: ShieldCheck,
    title: "Editorial Review",
    desc: "Every article is reviewed by the MediaCrypto editorial team before publication. Material financial, regulatory, and factual claims are checked against authoritative sources.",
  },
  {
    icon: RefreshCw,
    title: "Corrections Policy",
    desc: "We correct errors promptly and transparently. If you spot an inaccuracy in our coverage, contact us and we will review and update the article as needed.",
  },
];

export default function ResearchDesk() {
  return (
    <div className="pt-24 pb-20 max-w-3xl mx-auto px-6">
      <div className="text-center mb-12">
        <div className="w-16 h-16 rounded-2xl bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center mx-auto mb-5">
          <BookOpen className="w-7 h-7 text-yellow-400" />
        </div>
        <h1 className="font-display font-bold text-4xl text-[var(--text-primary)]">MediaCrypto Research Desk</h1>
        <p className="mt-4 text-lg text-[var(--text-secondary)] leading-relaxed max-w-xl mx-auto">
          The MediaCrypto Research Desk covers digital asset markets, blockchain infrastructure, crypto regulation, and market structure.
        </p>
      </div>

      <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-8 mb-10">
        <p className="text-[var(--text-secondary)] leading-relaxed">
          Articles published under the MediaCrypto Research Desk byline are independently researched using primary regulatory documents,
          blockchain data, market data providers, and company disclosures. AI-assisted tools may support research organization and drafting,
          but published analysis is reviewed and fact-checked by the editorial team before publication. Material financial, regulatory, and
          market claims are checked against authoritative sources rather than republished from secondary coverage.
        </p>
      </div>

      <h2 className="font-display font-bold text-2xl text-[var(--text-primary)] mb-6">How We Work</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
        {PRINCIPLES.map((p) => (
          <div key={p.title} className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-6">
            <p.icon className="w-5 h-5 text-yellow-400 mb-3" />
            <h3 className="font-display font-bold text-[var(--text-primary)] mb-2">{p.title}</h3>
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{p.desc}</p>
          </div>
        ))}
      </div>

      <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-8 text-center">
        <h2 className="font-display font-bold text-xl text-[var(--text-primary)] mb-3">Read Our Full Editorial Standards</h2>
        <p className="text-sm text-[var(--text-secondary)] mb-5">Sourcing, corrections, AI usage, and conflict-of-interest policy in detail.</p>
        <Link to="/editorial-policy" className="inline-block bg-yellow-400 text-black font-bold uppercase tracking-wide rounded-full px-6 py-3 hover:bg-yellow-500 transition text-sm">
          View Editorial Policy
        </Link>
      </div>

      <p className="text-xs text-[var(--text-tertiary)] text-center mt-10">
        Have a correction or tip? <Link to="/about" className="text-yellow-400 hover:underline">Contact us</Link>.
      </p>
    </div>
  );
}